import { useEffect } from "react";
import {
  AccessibilityInfo,
  Platform,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import { Icon, type IconName } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

// THE shared "cue pill": one tinted, rounded, bordered strip carrying a short
// glanceable message, optionally with a leading glyph, trailing forward
// actions, and a dismiss ✕. It collapses the hand-rolled brand-pill copies
// flagged in next/consolidation.md (CN-brand-pill): the logger advance cue
// (FocusPanel.cueRow) and the fail cue share this box instead of re-rolling
// brand100/brand500/r2 per site. (FocusPanelEffort.ctaPill stays separate:
// its fill is a translucent LAYER so the effort scale shows through, a
// distinct requirement.)
//
// `tone` picks the token family, never colour alone: `brand` = "you moved
// forward" (the advance cue); `danger` = a fail notice, matching the
// Badge tone="danger" + icon="x" vocabulary the timeline already uses for a
// failed set; `warn` = an offer worth a look (the rest invite). A leading
// icon reinforces the tone (shape, not colour, is the primary cue for
// reduced vision).
//
// Layout adapts to content: a bare message hugs its content (left-aligned pill,
// the advance-cue look); adding an action or a dismiss stretches it to a
// full-width strip with the controls pushed to the right.

export type CuePillTone = "brand" | "danger" | "warn";

export type CuePillAction = {
  label: string;
  onPress: () => void;
  icon?: IconName;
  accessibilityLabel?: string;
  /** Muted + inert while the action's own work is in flight. A caller can
   *  already ignore a repeat tap, but that is not visible: a control that
   *  looks live and answers nothing invites the second tap it just swallowed. */
  disabled?: boolean;
};

export type CuePillProps = {
  theme: UiTheme;
  /** The message text. */
  label: string;
  /** Token family + semantic. Defaults to `brand`. */
  tone?: CuePillTone;
  /** Optional leading glyph, coloured from the tone (never pass a colour). */
  icon?: IconName;
  /** Text weight: 800 for a bare glanceable cue, 600 for a multi-word banner.
   *  Defaults to 800. */
  weight?: "600" | "800";
  /** Message text size. `sm` for a glanceable transient cue; `base` for a
   *  persistent, higher-stakes message that should read bigger at reduced
   *  vision. Defaults to `sm`. */
  size?: "sm" | "base";
  /** Vertical padding: `s1` (dense, the advance cue) or `s2` (a roomier
   *  banner). Defaults to `s1`. */
  padY?: "s1" | "s2";
  /** Announce the text to assistive tech when it appears (a status message),
   *  on every platform. See the announce effect below. Off by default so the
   *  high-frequency advance cue stays quiet. */
  live?: boolean;
  /** Optional trailing forward action — rendered as a compact secondary
   *  Button (neutral, not destructive: "move on" is not a dangerous act). */
  action?: CuePillAction;
  /** Two forward actions instead of one, for a strip that offers a fix AND a
   *  way to look at what went wrong (the DX-2 stuck-writes signal: "retry now"
   *  next to "details"). Wins over `action` when both are passed. Kept to two
   *  on purpose — a third would not survive the long locales next to the
   *  message on a narrow screen. */
  actions?: readonly CuePillAction[];
  /** Optional dismiss ✕ (muted IconButton). */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Layout-only override (alignSelf/margins). Merged last. */
  style?: StyleProp<ViewStyle>;
};

// `text` is the message colour, `accent` the border + leading glyph. They
// split for danger: `danger` text on `dangerFill` is 4.62:1 on brand-light
// (just over the 4.5 floor), so `ink` (>=5.4:1 everywhere) still carries the
// message for margin and consistency, while the danger signal rides on the
// border + "x" glyph (a graphical element, 3:1 floor, cleared at 4.62).
// Brand carries one tone across both: `brandText`,
// the role DS-11 split out of the old dual-role brand600 precisely for this
// pairing — brand text ON brand100, which is the pill's own tint. brand600 sat
// at 3.51:1 there on brand-light while this pill gates a WRITE gesture (the
// AMRAP band); brandText is gated >=4.5:1 against brand100 in every family.
const TONE: Record<
  CuePillTone,
  (p: UiTheme["palette"]) => { bg: string; border: string; text: string; accent: string }
> = {
  brand: (p) => ({ bg: p.brand100, border: p.brand500, text: p.brandText, accent: p.brandText }),
  danger: (p) => ({ bg: p.dangerFill, border: p.danger, text: p.ink, accent: p.danger }),
  // `warn` = an offer worth noticing that is not an error (the rest invite).
  // Same split as danger: `ink` carries the text, since the (warn, warnFill)
  // text pair has no gate (lowest 4.63:1, rosepine light); the gold rides on
  // the border and the glyph.
  warn: (p) => ({ bg: p.warnFill, border: p.warn, text: p.ink, accent: p.warn }),
};

export function CuePill({
  theme,
  label,
  tone = "brand",
  icon,
  weight = "800",
  size = "sm",
  padY = "s1",
  live = false,
  action,
  actions,
  onDismiss,
  dismissLabel,
  style,
}: CuePillProps) {
  const styles = makeStyles(theme, size);
  const c = TONE[tone](theme.palette);
  // `accessibilityLiveRegion` below is inert on iOS, so iOS (and only iOS,
  // or TalkBack says it twice) gets the text pushed here as well. Same split
  // as Library/LibraryResults.tsx.
  useEffect(() => {
    if (!live || Platform.OS !== "ios") return;
    AccessibilityInfo.announceForAccessibility(label);
  }, [live, label]);
  const acts: readonly CuePillAction[] =
    actions ?? (action ? [action] : []);
  const hasControls = acts.length > 0 || !!onDismiss;
  // Adjacent controls: full 8 hitSlop on the OUTER edges, 2 on every shared
  // inner edge so neighbouring zones meet the visual gap (s2 = 4) without
  // overlapping. That keeps each Button and the 36-box ✕ at ≥44 effective
  // touch width, instead of collapsing horizontal slop to zero (which left the
  // ✕ at 36pt, under the floor). Generalised from the original two-control
  // case when DX-2 needed a strip carrying two actions.
  const controlCount = acts.length + (onDismiss ? 1 : 0);
  const slopAt = (i: number) => ({
    top: 8,
    bottom: 8,
    left: i === 0 ? 8 : 2,
    right: i === controlCount - 1 ? 8 : 2,
  });

  return (
    <View
      style={[
        styles.base,
        { backgroundColor: c.bg, borderColor: c.border },
        { paddingVertical: padY === "s2" ? theme.spacing.s2 : theme.spacing.s1 },
        hasControls ? styles.full : styles.hug,
        style,
      ]}
    >
      <View style={[styles.msg, hasControls && styles.msgBeside]}>
        {icon ? (
          <Icon name={icon} size={theme.typography.sizes.md} color={c.accent} />
        ) : null}
        <Text
          style={[
            styles.text,
            { color: c.text, fontWeight: weight },
            size === "base" ? styles.textBase : styles.textSm,
          ]}
          accessibilityLiveRegion={live ? "polite" : "none"}
        >
          {label}
        </Text>
      </View>
      {hasControls ? (
        <View style={styles.controls}>
          {acts.map((a, i) => (
            <Button
              // Index, not the label: labels are translated strings, and two
              // actions could legitimately share one.
              key={`action-${String(i)}`}
              theme={theme}
              compact
              variant="secondary"
              label={a.label}
              icon={a.icon}
              onPress={a.onPress}
              disabled={a.disabled}
              style={styles.control}
              accessibilityLabel={a.accessibilityLabel ?? a.label}
              hitSlop={slopAt(i)}
            />
          ))}
          {onDismiss ? (
            <IconButton
              theme={theme}
              variant="cancel"
              name="close"
              onPress={onDismiss}
              accessibilityLabel={dismissLabel ?? label}
              hitSlop={slopAt(controlCount - 1)}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme, size: "sm" | "base") {
  const { spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    base: {
      flexDirection: "row",
      alignItems: "center",
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
    },
    // Bare cue: hug the content, left-aligned (the original advance-cue look).
    hug: { alignSelf: "flex-start" },
    // With controls: full-width strip, message left, controls pushed right.
    // `flexWrap`: when the message cannot keep its floor width (`msg`) beside
    // the controls (320 px in ru, or a 1.5 font scale), the controls drop
    // under it instead of being drawn over it.
    full: {
      alignSelf: "stretch",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: s.s2,
    },
    // The message shrinks (and wraps) so the controls keep their room, down to
    // ~14 characters; below that the controls take the next line. `flexBasis: 0`
    // makes that floor, not the full text, the width the wrap decision sees, so
    // a long message still sits BESIDE the controls where it did before.
    msg: { flexDirection: "row", alignItems: "center", gap: s.s2, flexShrink: 1 },
    msgBeside: { flexGrow: 1, flexBasis: 0, minWidth: ty.sizes[size] * 14 },
    // flexShrink so the longest locale (DE) wraps within the strip rather than
    // clipping or shoving the controls off-screen. Font size/lineHeight come
    // from the `size` variant below.
    text: { flexShrink: 1 },
    textSm: { fontSize: ty.sizes.sm, lineHeight: ty.sizes.sm * 1.4 },
    textBase: { fontSize: ty.sizes.base, lineHeight: ty.sizes.base * 1.4 },
    // `marginLeft: auto` keeps them right-aligned on a line of their own.
    controls: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      marginLeft: "auto",
      // Never wider than the strip: the buttons share what is there and wrap
      // their labels (320 px, ru, font 1.5).
      flexShrink: 1,
      maxWidth: "100%",
    },
    control: { flexShrink: 1 },
  });
}
